import { Observable, map, shareReplay } from 'rxjs';
import { environment } from 'src/environments/environment';

import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Run } from './run.model';
import { MtiBreakdownResult } from '../shared/models/mti-comparison.model';
import { InventoryModelDeliveryOutput } from '../shared/models/inventory-model-delivery-output.model';

const INVENTORY_DELIVERIES_SECTION = 'outputs.InventoryModelSolution.deliveries';
const ROTATION_OUTPUT_SECTION = 'outputs.RotationOutput';

@Injectable({ providedIn: 'root' })
export class RunsService {
    private api = `${environment.applicationApi}/v1`;
    private berthRunsUrl = environment.berthScheduleApi.runsUrl;
    private readonly inventoryDeliveriesByRun = new Map<string, Observable<unknown>>();
    private readonly rotationOutputsByRun = new Map<string, Observable<unknown>>();

    constructor(private httpClient: HttpClient) { }

    getRuns(): Observable<Run[]> {
        return this.httpClient.get<Run[]>(`${this.api}/Runs`);
    }

    getBerthRuns(): Observable<Run[]> {
        return this.httpClient.get<Run[]>(this.berthRunsUrl);
    }

    getInventoryOptimisationRuns(): Observable<Run[]> {
        return this.httpClient.get<Run[]>(`${this.berthRunsUrl}/inventory-optimisation`);
    }

    getRun(runId: string): Observable<Run> {
        return this.httpClient.get<Run>(`${this.api}/Runs/${runId}`);
    }

    getInventoryModelDeliveries(runId: string): Observable<InventoryModelDeliveryOutput[]> {
        return this.getInventoryDeliveriesSection(runId).pipe(
            map(response => {
                if (Array.isArray(response)) {
                    return response as InventoryModelDeliveryOutput[];
                }

                const message = this.sectionError(response)
                    ?? 'This run does not contain an inventory optimisation result.';
                throw new Error(message);
            })
        );
    }

    getRotationOutput(runId: string): Observable<unknown> {
        let response = this.rotationOutputsByRun.get(runId);
        if (!response) {
            response = this.getModelSection(runId, ROTATION_OUTPUT_SECTION).pipe(
                shareReplay({ bufferSize: 1, refCount: false })
            );
            this.rotationOutputsByRun.set(runId, response);
        }
        return response;
    }

    private getInventoryDeliveriesSection(runId: string): Observable<unknown> {
        let response = this.inventoryDeliveriesByRun.get(runId);
        if (!response) {
            response = this.getModelSection(runId, INVENTORY_DELIVERIES_SECTION).pipe(
                shareReplay({ bufferSize: 1, refCount: false })
            );
            this.inventoryDeliveriesByRun.set(runId, response);
        }
        return response;
    }

    private getModelSection(runId: string, section: string): Observable<unknown> {
        const params = new HttpParams()
            .set('file', 'model')
            .set('section', section);
        return this.httpClient.get<unknown>(`${this.berthRunsUrl}/${runId}/output`, { params });
    }

    private sectionError(response: unknown): string | null {
        if (!response || typeof response !== 'object' || !('error' in response)) return null;
        const error = (response as { error?: unknown }).error;
        return typeof error === 'string' ? error : null;
    }

    // Inter-run. The page shows counts only, so it skips the shifted deliveries (a second read of both runs' files).
    getMtiInterBreakdown(leftRunId: string, leftSource: 'outputs'|'inputs', rightRunId: string, rightSource: 'outputs'|'inputs'): Observable<MtiBreakdownResult> {
        const params = new HttpParams()
            .set('leftRunId', leftRunId)
            .set('leftSource', leftSource)
            .set('rightRunId', rightRunId)
            .set('rightSource', rightSource)
            .set('includeShiftedDeliveries', false);
        return this.httpClient.get<MtiBreakdownResult>(`${this.api}/Mti/compare/breakdown`, { params });
    }
}
